import { useFormikContext } from 'formik';
import React, { useMemo, useEffect } from 'react';
import { FormValues } from './AppointmentModal';
import Button from '../../BaseComponents/Button';
import DatePickerComp from '../../BaseComponents/DatePicker';
import AppointmentLocation from './AppointmentLocation';
import { ReactComponent as BinIcon } from '../../../assets/icons/TrashBin2.svg';
import TextInput from '../../BaseComponents/TextInput';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  AppointmentAssigneeType,
  AppointmentStatus,
  LocationType
} from '../../../types/apiGeneratedTypes';
import {
  changeDeleteModalVisibility,
  changeEditModalVisibility
} from '../../../store/appointmentsSlice';
import Select from '../../BaseComponents/Select';
import { statusOptions, statusTranslations } from '../../../utils/constants';

type AssigneeOption = { key: string; value: string };

type AppointmentFormProps = {
  currentLocationOption: LocationType;
  onLocationBtnClick: any;
  isSubmitting: any;
  isFormError: string | null;
  membershipType?: string | null;
  doctorsOptions: AssigneeOption[];
  nutritionistsOptions: AssigneeOption[];
  defaultDoctorAssignee: AssigneeOption | null;
  defaultNutritionistAssignee: AssigneeOption | null;
};

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  currentLocationOption,
  onLocationBtnClick,
  isSubmitting,
  isFormError,
  membershipType,
  doctorsOptions,
  nutritionistsOptions,
  defaultDoctorAssignee,
  defaultNutritionistAssignee,
}) => {
  const dispatch = useDispatch();
  const { appointmentIdToEdit } = useSelector(
    (state: RootState) => state.appointments
  );
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<FormValues>();

  useEffect(() => {
    if (currentLocationOption === LocationType.CLINIC) {
      setFieldValue(
        'preparation_text',
        `CV Blood Test Preparation:
This clinic appointment is for your blood sample to be collected.
Here are a few tips to help you prepare for the appointment:

- Don't forget to take your blood collection kit with you to your appointment! Your kit contains everything the clinic needs to take your sample.
- Make sure you are well hydrated. Try to drink a large glass of water an hour before your appointment. This will make it easier for the clinic to collect your blood.'
- Wear short sleeves if possible. Your nurse will probably take the blood from the inside of your arm and will need to place a tourniquet above this as well, so it is best if this area is easily accessible.`
      );
    } else if (currentLocationOption === LocationType.HISTORICRESULTS) {
      setFieldValue('preparation_text', '');
    } else if (currentLocationOption === LocationType.MEMBER_HOME) {
      setFieldValue(
        'preparation_text',
        `Here are a few tips to help you prepare for the appointment:

- Make sure you are well hydrated. Try to drink a large glass of water an hour before your appointment. This will make it easier for the nurse to collect your blood.
- Wear short sleeves if possible. Your nurse will probably take the blood from the inside of your arm and will need to place a tourniquet above this as well, so it is best if this area is easily accessible.`
      );
    } else if (currentLocationOption === LocationType.ONLINE) {
      setFieldValue('preparation_text', '');
    } else if (currentLocationOption === LocationType.SCAN) {
      setFieldValue(
        'preparation_text',
        `24 Hours Before
• Hydrate: Drink water normally.
• Implants: Ensure you have discussed any implants (pacemakers, pins, cochlear implants) during your safety screen.
4 Hours Before
• Food: Stop eating (unless advised otherwise during your consultationconsult). A clear stomach provides better imaging of the abdomen.
• Fluids: You may continue to drink water.
Arrival
• Arrive 30 minutes early to complete safety paperwork at the clinic.`
      );
    }
  }, [currentLocationOption, setFieldValue]);

  useEffect(() => {
    if (currentLocationOption !== LocationType.ONLINE) return;
    if (values.assignee?.key) return;
    const fallback =
      values.assignee_type === AppointmentAssigneeType.DOCTOR
        ? defaultDoctorAssignee
        : defaultNutritionistAssignee;
    if (fallback?.key) {
      setFieldValue('assignee', fallback);
    }
  }, [
    currentLocationOption,
    values.assignee_type,
    values.assignee?.key,
    defaultDoctorAssignee,
    defaultNutritionistAssignee,
    setFieldValue,
  ]);

  useEffect(() => {
    // When editing an appointment we may have only an id at first; once options arrive,
    // swap in the matching {key,value} so the Select can display the name.
    if (currentLocationOption !== LocationType.ONLINE) return;
    const key = values.assignee?.key;
    if (!key) return;
    const currentValue = values.assignee?.value;
    if (currentValue && currentValue !== key) return;

    const doctorMatch = doctorsOptions.find((o) => String(o.key) === String(key));
    const nutritionistMatch = nutritionistsOptions.find(
      (o) => String(o.key) === String(key)
    );

    if (nutritionistMatch) {
      if (values.assignee_type !== AppointmentAssigneeType.NUTRITIONIST) {
        setFieldValue('assignee_type', AppointmentAssigneeType.NUTRITIONIST);
      }
      setFieldValue('assignee', nutritionistMatch);
      return;
    }
    if (doctorMatch) {
      if (values.assignee_type !== AppointmentAssigneeType.DOCTOR) {
        setFieldValue('assignee_type', AppointmentAssigneeType.DOCTOR);
      }
      setFieldValue('assignee', doctorMatch);
    }
  }, [
    currentLocationOption,
    values.assignee?.key,
    values.assignee?.value,
    values.assignee_type,
    doctorsOptions,
    nutritionistsOptions,
    setFieldValue,
  ]);

  const isSubmitBtnDisabled = useMemo(() => {
    if (!values.title || !values.time || !values.status) return true;

    switch (currentLocationOption) {
      case LocationType.CLINIC:
        return !values.clinic.value;
      case LocationType.ONLINE:
        return !values.meeting_link || !values.assignee?.key;
      case LocationType.MEMBER_HOME:
        return false;
      case LocationType.SCAN:
        return !values.clinic.value;
      default:
        return false;
    }
  }, [
    currentLocationOption,
    values.clinic.value,
    values.meeting_link,
    values.time,
    values.title,
    values.status,
    values.assignee?.key,
  ]);

  const onDeleteAppointmentClick = () => {
    dispatch(changeEditModalVisibility(false));
    dispatch(changeDeleteModalVisibility(true));
  };

  return (
    <form className='appointment-form' onSubmit={handleSubmit}>
      <div className='appointments-form'>
        <div className='title-wrapper'>
          <div className='section-title'>Title *</div>
          <TextInput
            placeholder='Name this appointment'
            name='title'
            type='text'
            onChange={(e) => setFieldValue('title', e.target.value)}
            value={values.title}
          />
        </div>
        <div className='title-wrapper'>
          <div className='section-title'>Status *</div>
          <Select
            name='section'
            placeholder='Select section'
            options={statusOptions.map((status) => ({
              key: status.value,
              value: status.label
            }))}
            selectedOption={{
              key: values.status,
              value: values.status
                ? statusTranslations[values.status as AppointmentStatus]
                : ''
            }}
            onChange={(option) => {
              setFieldValue('status', option.key);
            }}
            isError={false}
          />
        </div>
        <AppointmentLocation
          currentLocationOption={currentLocationOption}
          onLocationBtnClick={onLocationBtnClick}
          membershipType={membershipType}
        />
        {currentLocationOption === LocationType.ONLINE && (
          <div className='top-space appointment-assignee-section'>
            <div className='section-title'>Assignee</div>
            <div className='appointment-assignee-fields'>
              <div
                role='radiogroup'
                aria-label='Appointment assignee type'
                className='appointment-assignee-radio-group'
              >
                <label className='appointment-assignee-radio-label'>
                  <input
                    type='radio'
                    name='assignee_type'
                    checked={values.assignee_type === AppointmentAssigneeType.DOCTOR}
                    onChange={() => {
                      setFieldValue('assignee_type', AppointmentAssigneeType.DOCTOR);
                      const currentKey = values.assignee?.key;
                      const keepCurrent =
                        currentKey &&
                        doctorsOptions.some((o) => String(o.key) === String(currentKey));
                      if (keepCurrent) return;
                      setFieldValue(
                        'assignee',
                        defaultDoctorAssignee?.key
                          ? defaultDoctorAssignee
                          : { key: '', value: '' }
                      );
                    }}
                  />
                  <span>Doctor</span>
                </label>
                <label className='appointment-assignee-radio-label'>
                  <input
                    type='radio'
                    name='assignee_type'
                    checked={values.assignee_type === AppointmentAssigneeType.NUTRITIONIST}
                    onChange={() => {
                      setFieldValue('assignee_type', AppointmentAssigneeType.NUTRITIONIST);
                      const currentKey = values.assignee?.key;
                      const keepCurrent =
                        currentKey &&
                        nutritionistsOptions.some(
                          (o) => String(o.key) === String(currentKey)
                        );
                      if (keepCurrent) return;
                      setFieldValue(
                        'assignee',
                        defaultNutritionistAssignee?.key
                          ? defaultNutritionistAssignee
                          : { key: '', value: '' }
                      );
                    }}
                  />
                  <span>Nutritionist</span>
                </label>
              </div>
              <div className='appointment-assignee-select-wrap'>
                <Select
                  name='assignee'
                  placeholder={
                    values.assignee_type === AppointmentAssigneeType.DOCTOR
                      ? 'Select doctor *'
                      : 'Select nutritionist *'
                  }
                  options={
                    (values.assignee_type === AppointmentAssigneeType.DOCTOR
                      ? doctorsOptions
                      : nutritionistsOptions) || []
                  }
                  selectedOption={values.assignee}
                  onChange={(option) => setFieldValue('assignee', option)}
                  isError={false}
                />
              </div>
            </div>
          </div>
        )}
        <div className='date-and-time'>
          <DatePickerComp
            title='Date & Time *'
            placeholder='Select date and time'
            selectedDate={values.time}
            showTimeInput={true}
            showMonthDropdown={true}
            showYearDropdown={true}
            showUKTime={true}
            onDateChange={(e) => {
              setFieldValue('time', e);
            }}
          />
        </div>
        {currentLocationOption === LocationType.ONLINE && (
          <div className='top-space'>
            <div className='section-title'>Preparation recommendations</div>
            <div className='text-area-input-wrapper'>
              <textarea
                className='textarea-input'
                name='preparation_text'
                rows={10}
                value={values.preparation_text}
                onChange={(e) =>
                  setFieldValue('preparation_text', e.target.value)
                }
                placeholder='Enter preparation recommendations for online appointment'
              />
            </div>
          </div>
        )}
        {currentLocationOption === LocationType.HISTORICRESULTS && (
          <div className='top-space'>
            <div className='section-title'>Preparation recommendations</div>
            <div className='text-area-input-wrapper'>
              <textarea
                className='textarea-input'
                name='preparation_text'
                rows={10}
                value={values.preparation_text || ''}
                onChange={(e) =>
                  setFieldValue('preparation_text', e.target.value)
                }
                placeholder='Enter preparation recommendations for historic results appointment'
              />
            </div>
          </div>
        )}
        {currentLocationOption === LocationType.MEMBER_HOME && (
          <div className='top-space'>
            <div className='section-title'>Preparation recommendations</div>
            <div className='text-area-input-wrapper'>
              <textarea
                className='textarea-input'
                name='preparation_text'
                rows={10}
                readOnly
                placeholder='Enter preparation recommendations for member home appointment'
                value={
                  values.preparation_text ||
                  `Here are a few tips to help you prepare for the appointment:

- Make sure you are well hydrated. Try to drink a large glass of water an hour before your appointment. This will make it easier for the nurse to collect your blood.
- Wear short sleeves if possible. Your nurse will probably take the blood from the inside of your arm and will need to place a tourniquet above this as well, so it is best if this area is easily accessible.`
                }
              />
            </div>
          </div>
        )}
        {currentLocationOption === LocationType.CLINIC && values.clinic && (
          <div className='clinic-appointment-details top-space'>
            <div className='section-title'>Preparation recommendations</div>
            <div className='text-area-input-wrapper'>
              <textarea
                className='textarea-input'
                name='preparation_text'
                rows={10}
                readOnly
                placeholder='Enter preparation recommendations for clinic appointment'
                value={
                  values.preparation_text ||
                  `CV Blood Test Preparation:
This clinic appointment is for your blood sample to be collected.
Here are a few tips to help you prepare for the appointment:

- Don't forget to take your blood collection kit with you to your appointment! Your kit contains everything the clinic needs to take your sample.
- Make sure you are well hydrated. Try to drink a large glass of water an hour before your appointment. This will make it easier for the clinic to collect your blood.
- Wear short sleeves if possible. Your nurse will probably take the blood from the inside of your arm and will need to place a tourniquet above this as well, so it is best if this area is easily accessible.`
                }
              />
            </div>
          </div>
        )}
        {currentLocationOption === LocationType.SCAN && values.clinic && (
          <div className='clinic-appointment-details top-space'>
            <div className='section-title'>Preparation recommendations</div>
            <div className='text-area-input-wrapper'>
              <textarea
                className='textarea-input'
                name='preparation_text'
                rows={10}
                readOnly
                placeholder='Enter preparation recommendations for scan appointment'
                value={
                  values.preparation_text ||
                  `24 Hours Before
• Hydrate: Drink water normally.
• Implants: Ensure you have discussed any implants (pacemakers, pins, cochlear implants) during your safety screen.
4 Hours Before
• Food: Stop eating (unless advised otherwise during your consultationconsult). A clear stomach provides better imaging of the abdomen.
• Fluids: You may continue to drink water.
Arrival
• Arrive 30 minutes early to complete safety paperwork at the clinic.`
                }
              />
            </div>
          </div>
        )}
        <div className='btn-wrapper'>
          <Button
            text={
              appointmentIdToEdit ? 'Update appointment' : 'Add appointment'
            }
            type='submit'
            isLoading={isSubmitting}
            isDisabled={isSubmitBtnDisabled}
          />
          {appointmentIdToEdit && (
            <div
              className='delete-btn-appointment'
              onClick={onDeleteAppointmentClick}
            >
              <BinIcon className='bin-icon-appointment' />
              <span>Delete appointment</span>
            </div>
          )}
          {isFormError && <div className='error-message'>{isFormError}</div>}
        </div>
      </div>
    </form>
  );
};
export default AppointmentForm;
